/**
 * TASK-ATT v1.3 — shared FE constants/helpers for Task + CCI attachments.
 * UX-only mirror of the backend allowlist (backend is the source of truth).
 */

/** Max files total (existing + new). */
export const MAX_ATTACHMENTS = 10;
/** Max bytes per file (20MB). */
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

/**
 * accept allowlist — EXACTLY these (W6/GPT#2,#3). NO svg, NO image/* wildcard,
 * NO legacy .doc/.xls/.ppt, NO macro. Used as the <input accept> value.
 */
export const ATTACHMENT_ACCEPT =
  "image/jpeg,image/png,image/webp,image/heic,.pdf,.docx,.xlsx,.pptx";

/** MIME types that render a thumbnail + «Переглянути» preview (NOT svg). */
const PREVIEWABLE_IMAGE_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
]);

/** True when the mime is a previewable image (image preview-modal eligible). */
export function isPreviewableImage(mime: string | undefined | null): boolean {
  if (!mime) return false;
  return PREVIEWABLE_IMAGE_MIME.has(mime.toLowerCase());
}

/**
 * True for PDF. PDF has NO in-app preview (a sandboxed iframe cannot load our
 * auth-blob without `allow-same-origin`, which would defeat isolation) — instead
 * it gets «Відкрити в новій вкладці» (top-level browser viewer). Office/doc = false.
 */
export function isPdf(mime: string | undefined | null): boolean {
  return (mime ?? "").toLowerCase() === "application/pdf";
}

/** MIME types played inline via `<video controls>` (header-auth blob). */
const PLAYABLE_VIDEO_MIME = new Set(["video/mp4", "video/quicktime"]);

/** True when the mime is an inline-playable video (mp4/mov). */
export function isVideo(mime: string | undefined | null): boolean {
  if (!mime) return false;
  return PLAYABLE_VIDEO_MIME.has(mime.toLowerCase());
}

/** Human-readable byte size (e.g. "1.2 МБ", "340 КБ"). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

/** Short date-time for uploadedAt; tolerant of null/invalid. */
export function formatAttachmentDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
